import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, fetchMyAttendance, fetchTodayAttendance } from '../api/client';
import type { AttendanceDay } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { fetchOrgRoster } from '../api/hbs';
import { checkModeOnError, resolveCheckMode, type CheckMode } from '../lib/check-mode';
import { displayNameOf, greeting } from '../lib/display-name';
import {
  homeCheckInGatedByNonWorkDay,
  homeSessionCard,
  type HomeSessionCard,
} from '../lib/home-session';
import { hariKerja } from '../lib/rekap-bulanan';
import { todayInZone, todayLookbackRange } from '../lib/attendance-session';
import {
  formatDateInZone,
  formatDayStatus,
  formatLateMinutes,
  formatTimeInZone,
  formatVerification,
  isUnverified,
} from '../lib/format';
import { hasAnyPermission } from '../lib/navigation';
import { isOversightRole, type WorkforceRole } from '../lib/worker-roles';
import { PerluPerhatian } from '../components/PerluPerhatian';
import { ErrorCard, LoadingState } from '../states';
import { Ringkasan } from './LaporanPage';

/**
 * Beranda lapangan — menjawab satu pertanyaan: **apa yang harus saya lakukan sekarang?**
 *
 * Karena itu tombol besarnya mengikuti keadaan sesi, bukan menampilkan Check In dan Check Out
 * berdampingan lalu membiarkan server menolak salah satunya.
 */
export function HomePage(): ReactNode {
  const session = useSession();
  /*
   * Nama disapa, bukan email atau id. Diambil dari roster HBS — nama milik HBS, dan `MeResponse`
   * hanya membawa id, peran, dan cakupan.
   *
   * Kegagalannya SENGAJA tidak berbunyi: beranda tanpa nama tetap beranda yang utuh, sedangkan
   * kartu galat karena sapaan adalah gangguan yang tidak sebanding dengan nilainya.
   */
  const [nama, setNama] = useState<string | null>(null);
  useEffect(() => {
    let aktif = true;
    fetchOrgRoster(session.auth.accessToken, session.me.organizationId)
      .then((roster) => {
        if (aktif) setNama(displayNameOf(roster, session.me.userId));
      })
      .catch(() => {
        /* diam — lihat alasan di atas */
      });
    return () => {
      aktif = false;
    };
  }, [session.auth.accessToken, session.me.organizationId, session.me.userId]);
  const timezone = session.policy?.timezone ?? 'Asia/Jakarta';
  const today = todayInZone(new Date(), timezone);

  const [state, setState] = useState<
    | { status: 'loading' }
    | {
        status: 'ready';
        kartu: HomeSessionCard;
        days: readonly AttendanceDay[];
        mode: CheckMode;
      }
    | { status: 'error'; error: ApiError }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    const range = todayLookbackRange(today);
    /*
     * DUA permintaan, dan sengaja dengan kegagalan yang TERPISAH.
     *
     * Keadaan sesi datang dari `/api/me/attendance/today` — satu-satunya endpoint yang benar-benar
     * menjawabnya, memakai kebijakan dan zona waktu organisasi. Sebelumnya beranda menyimpulkannya
     * dari `/api/attendance/me`, yang mengembalikan `AttendanceDay[]` telanjang dan tidak pernah
     * membawa event; akibatnya kartu terbesar di layar ini berbunyi "belum dapat dibaca dari
     * server" SETIAP HARI. Terlihat pada sapuan Chrome 22 Agu — bukan dari membaca kode, karena
     * di kode cabang itu tampak seperti kasus darurat.
     *
     * `allSettled`, bukan `all`: ringkasan hari yang gagal tidak boleh ikut menghapus jawaban
     * "apa yang harus saya lakukan sekarang", dan sebaliknya. Itu pertanyaan yang membawa orang
     * ke layar ini.
     */
    void Promise.allSettled([
      fetchTodayAttendance(session.auth),
      fetchMyAttendance(session.auth, range.from, range.to),
    ]).then(([hariIni, riwayat]) => {
      if (cancelled) return;

      // Kedua-duanya gagal: tidak ada yang dapat dikatakan dengan jujur selain galatnya.
      if (hariIni.status === 'rejected' && riwayat.status === 'rejected') {
        const e = hariIni.reason;
        setState({
          status: 'error',
          error:
            e instanceof ApiError
              ? e
              : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
        });
        return;
      }

      const mode =
        hariIni.status === 'fulfilled'
          ? resolveCheckMode(hariIni.value.nextAction)
          : checkModeOnError();
      setState({
        status: 'ready',
        kartu: homeSessionCard(
          mode,
          hariIni.status === 'fulfilled' ? hariIni.value.openSessionStartedOnEarlierDay : false,
          hariIni.status === 'rejected',
        ),
        days: riwayat.status === 'fulfilled' ? riwayat.value.days : [],
        mode,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [session.auth, today]);

  const canCheck = hasAnyPermission(session.me.permissions, [
    'workforce.attendance.check_in',
    'workforce.attendance.check_out',
  ]);
  /*
   * Owner/Manajer (item 5, review 12 Sep 2026): kartu Status sesi TETAP di atas (K2 — keduanya
   * tetap wajib absen), tapi "Hari ini" milik Kurir (jam masuk besar) diganti Ringkasan
   * organisasi/tim — pertanyaan yang benar-benar dibawa peran ini ke beranda bukan "jam berapa
   * saya masuk", melainkan "bagaimana keadaan tim/organisasi hari ini".
   */
  const oversight = isOversightRole(session.me.roles as readonly WorkforceRole[]);

  /*
   * Hari libur (item 8, review 12 Sep 2026) — PEMBALIKAN keputusan sebelumnya (`work_days` dulu
   * sengaja tidak pernah menolak check-in). `hariKerja` dipakai ulang dari `rekap-bulanan.ts`,
   * BUKAN dihitung ulang di sini — fungsi yang sama sudah menjaga dirinya dari pergeseran
   * timezone (`workDays` kosong/tidak dikenal → SEMUA hari dianggap hari kerja, jangan menebak).
   *
   * HANYA memengaruhi tombol saat tindakan berikutnya memang CHECK_IN — absen pulang (K2 & ADR-
   * 0034 §5) tidak pernah ditahan oleh gerbang ini, persis seperti server.
   */
  const hariIniLibur = session.policy ? !hariKerja(today, session.policy.workDays) : false;
  const absenMasukDitahanHariLibur =
    state.status === 'ready' &&
    homeCheckInGatedByNonWorkDay(
      state.mode,
      hariIniLibur,
      session.policy?.allowCheckInOnNonWorkDays ?? true,
    );

  return (
    <>
      <section className="card">
        <p className="muted small">{formatDateInZone(new Date().toISOString(), timezone)}</p>
        <h2>{greeting(nama)}</h2>
        {session.policy === null ? (
          <p className="warn-text">
            Organisasi Anda belum mengaktifkan kebijakan absensi, sehingga absen masuk belum dapat
            dilakukan. Hubungi admin organisasi.
          </p>
        ) : null}
      </section>

      {/*
        DI ATAS status sesi, DI BAWAH sapaan. Pengajuan yang menunggu keputusan adalah hal pertama
        yang berubah sejak kemarin; status absen sendiri jarang mengejutkan pembacanya.
      */}
      <PerluPerhatian />

      {state.status === 'loading' ? <LoadingState label="Memuat status hari ini…" /> : null}
      {state.status === 'error' ? <ErrorCard error={state.error} /> : null}

      {state.status === 'ready' ? (
        <>
          <section className="card">
            <h3>Status sesi</h3>
            {/*
              Satu kalimat, dari server, tanpa tebakan. `menonjol` disediakan `homeSessionCard` —
              bukan dihitung di sini — supaya "kapan layar ini boleh memperingatkan" punya satu
              tempat yang dapat diuji. Sebelumnya peringatan menyala setiap hari, dan peringatan
              yang selalu menyala berhenti dibaca sebagai peringatan.
            */}
            <p className={state.kartu.menonjol ? 'warn-text' : 'muted'}>{state.kartu.kalimat}</p>

            {/*
              Item 8, review 12 Sep 2026. Ditampilkan terlepas dari `mode` — "hari ini bukan hari
              kerja" adalah fakta kalender, bukan sesuatu yang hanya berarti bagi absen masuk.
            */}
            {hariIniLibur ? <p className="muted small">Hari ini bukan hari kerja.</p> : null}

            {canCheck && !absenMasukDitahanHariLibur ? (
              /*
                Owner/Manajer (#1758): ukuran biasa, bukan selebar layar — bagi pengawas absen
                masuk bukan tindakan utama Beranda; yang utama kartu "Perlu perhatian" di atas.
                Pekerja lapangan tetap mendapat tombol besar: bagi mereka ini memang tujuan utama.
              */
              <Link
                className={oversight ? 'button-primary' : 'button-primary big'}
                to="/attendance/check"
              >
                {state.kartu.tombol}
              </Link>
            ) : canCheck ? (
              /*
               * Tombol DISEMBUNYIKAN, bukan sekadar dimatikan — server pun menolak dengan pesan
               * yang SAMA (`NonWorkDayCheckInNotAllowedError`, ATT-013) bila tetap dipaksa lewat
               * jalur lain. Menyembunyikan tanpa server ikut menegakkan hanya memindahkan
               * penolakan ke belakang, bukan menghilangkannya — dan justru itu yang TIDAK
               * diminta di sini.
               */
              <p className="muted">
                Absen masuk tidak tersedia hari ini — kebijakan organisasi tidak mengizinkan absen
                masuk di luar hari kerja.
              </p>
            ) : (
              <p className="muted">
                Peran Anda tidak mencakup absen masuk maupun absen pulang di organisasi ini.
              </p>
            )}
          </section>

          {oversight ? (
            <Ringkasan />
          ) : (
            <TodaySummary days={state.days} today={today} timezone={timezone} />
          )}
        </>
      ) : null}
    </>
  );
}

function TodaySummary({
  days,
  today,
  timezone,
}: {
  readonly days: readonly AttendanceDay[];
  readonly today: string;
  readonly timezone: string;
}): ReactNode {
  const day = days.find((d) => d.workDate === today) ?? null;
  const masuk = day?.firstCheckInAt ? formatTimeInZone(day.firstCheckInAt, timezone) : null;
  /*
   * Kartu utama layar, bentuk dari desain 22 Agu: label mikro, satu ANGKA BESAR, keterangan.
   *
   * Angka besarnya adalah JAM MASUK, bukan status. Status sudah tertulis sebagai kata di
   * bawahnya, dan mengulanginya sebagai angka tidak menambah apa pun; jam masuk justru
   * pertanyaan yang benar-benar ditanyakan orang pada dirinya sendiri di tengah hari kerja
   * ("tadi saya masuk jam berapa?"). Tanda pisah dipakai bila belum ada — bukan "00:00", yang
   * terbaca seperti masuk tengah malam.
   */
  return (
    <section className="card accent">
      <p className="eyebrow">Hari ini</p>
      {/*
        Tanpa jam masuk, slot angka besar DIKOSONGKAN — bukan diisi tanda pisah.
        Terlihat pertama kali di produksi 22 Agu: `—` yang mewarisi gaya angka 2rem menjadi garis
        tebal menggantung yang terbaca seperti kesalahan render, bukan seperti "belum ada". Tidak
        tertangkap harness karena di sana slotnya selalu berisi jam sungguhan; keadaan kosongnya
        hanya muncul pada data nyata.
      */}
      {masuk === null ? (
        <p className="figure-kosong">Belum ada absen masuk tercatat</p>
      ) : (
        <>
          <p className="figure">{masuk}</p>
          <p className="muted small">Absen masuk · {formatDayStatus(day?.status ?? null)}</p>
        </>
      )}
      <dl className="pairs" style={{ marginTop: '1rem' }}>
        <dt>Absen pulang</dt>
        <dd>{day?.lastCheckOutAt ? formatTimeInZone(day.lastCheckOutAt, timezone) : '—'}</dd>
        {day && day.lateMinutes > 0 ? (
          <>
            <dt>Keterlambatan</dt>
            <dd>{formatLateMinutes(day.lateMinutes)}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

/** Dipakai layar lain kelak; diekspor di sini agar aturan penonjolan tetap satu tempat. */
export function verificationBadge(verification: string): ReactNode {
  return (
    <span className={isUnverified(verification) ? 'badge warn' : 'badge ok'}>
      {formatVerification(verification)}
    </span>
  );
}
